import { SubmitHandler } from "react-hook-form";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  PaymentTypes,
  StatisticsType,
  StudioType,
  UserRole,
} from "../../../common/enums";
import { StatisticsTypeLabels } from "../../../common/labels";
import { useStatisticsForm } from "./useStatisticsForm";
import { useAuthentication } from "../../../common/infrastructure/hooks/useAuthentication";
import {
  ALL_HALLS_OPTION_ID,
  ALL_USERS_OPTION_ID,
  IStatisticsFormModel,
  isAllHallsSelected,
  isAllUsersSelected,
} from "../models/statisticsFormModel";
import {
  ALL_HALLS_URL_TOKEN,
  ALL_USERS_URL_TOKEN,
  constructViewStatisticsHref,
} from "../../../common/infrastructure/routes/routePaths";
import { dateUtility } from "../../../common/infrastructure/utilities/dateUtility";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { ISelectFieldModel } from "../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { useAppContext } from "../../../AppContext";

const parseHrefDate = (dateStr: string): Date | undefined => {
  if (!dateStr) return undefined;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return undefined;
  const [day, month, year] = parts.map(Number);
  if (!day || !month || !year) return undefined;
  return new Date(year, month - 1, day);
};

export const useStatisticsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasAutoNavigated = useRef(false);
  const { selectedStudio } = useAppContext();
  const { currentUser, currentUserRole } = useAuthentication();
  const tenantUsesMoreThanOneHall =
    selectedStudio?.tenantUsesMoreThanOneHall ?? false;

  // Only managers / tenants / global admins are allowed to view studio-wide
  // statistics across other employees. Lower-ranked roles are limited to
  // their own data — both in the dropdown UI and via API authorization.
  const canSeeAllEmployees = useMemo(() => {
    return (
      currentUserRole === UserRole.Employee_Manager ||
      currentUserRole === UserRole.Tenant ||
      currentUserRole === UserRole.GlobalAdmin
    );
  }, [currentUserRole]);

  const isStoreOnly =
    selectedStudio?.types?.length === 1 &&
    selectedStudio.types[0] === StudioType.Store;

  // НАП одиторският файл е видим само за обекти, приемащи Stripe плащания.
  const isStripeMerchant =
    selectedStudio?.supportedPaymentTypes?.includes(PaymentTypes.Stripe) ??
    false;

  const statisticTypeOptions = [
    {
      id: StatisticsType.Money,
      name: StatisticsTypeLabels[StatisticsType.Money],
    },
    // {
    //   id: StatisticsType.Plans,
    //   name: StatisticsTypeLabels[StatisticsType.Plans],
    // },
    // {
    //   id: StatisticsType.Users,
    //   name: StatisticsTypeLabels[StatisticsType.Users],
    // },
    ...(isStoreOnly
      ? []
      : [
          {
            id: StatisticsType.Event,
            name: StatisticsTypeLabels[StatisticsType.Event],
          },
        ]),
    {
      id: StatisticsType.Resources,
      name: StatisticsTypeLabels[StatisticsType.Resources],
    },
    ...(isStripeMerchant && canSeeAllEmployees
      ? [
          {
            id: StatisticsType.NapAudit,
            name: StatisticsTypeLabels[StatisticsType.NapAudit],
          },
        ]
      : []),
  ];

  const { data: availableEmployees } = useApiQuery<ISelectFieldModel[]>({
    queryKey: [endpointsMetadata.statistics.getAllEmployees],
    endpoint: endpointsMetadata.statistics.getAllEmployees,
    globalIsLoading: false,
  });

  const { data: availableHalls } = useApiQuery<ISelectFieldModel[]>({
    queryKey: [endpointsMetadata.studioHallTypeNomenclature.getAll],
    endpoint: endpointsMetadata.studioHallTypeNomenclature.getAll,
    enabled: tenantUsesMoreThanOneHall,
    globalIsLoading: false,
  });

  // Parse URL query params
  const urlType = searchParams.get("type");
  const urlDateFrom = searchParams.get("dateFrom");
  const urlDateTo = searchParams.get("dateTo");
  const urlUserIds = searchParams.get("userIds");
  const urlHallIds = searchParams.get("hallIds");

  const hasUrlParams =
    urlType != null && urlDateFrom != null && urlDateTo != null;

  const parsedType = urlType != null ? parseInt(urlType) : undefined;
  const parsedDateFrom = urlDateFrom ? parseHrefDate(urlDateFrom) : undefined;
  const parsedDateTo = urlDateTo ? parseHrefDate(urlDateTo) : undefined;
  // userIds: URL token "all" / null / empty maps to [ALL_USERS_OPTION_ID] for privileged
  // roles, or to the current user's own id for restricted roles. Otherwise parse comma list.
  const rawUserIds = !urlUserIds || urlUserIds === ALL_USERS_URL_TOKEN
    ? canSeeAllEmployees
      ? [ALL_USERS_OPTION_ID]
      : currentUser?.id != null
        ? [currentUser.id]
        : []
    : urlUserIds
        .split(",")
        .map(Number)
        .filter((n) => !isNaN(n) && n > 0);

  // hallIds: URL token "all" / null / empty maps to [ALL_HALLS_OPTION_ID]; otherwise parse comma list.
  const rawHallIds =
    !urlHallIds || urlHallIds === ALL_HALLS_URL_TOKEN
      ? [ALL_HALLS_OPTION_ID]
      : urlHallIds
          .split(",")
          .map(Number)
          .filter((n) => !isNaN(n) && n > 0);

  // Filter userIds to only those that exist in availableEmployees (preserving the "all" sentinel).
  const validUserIds = availableEmployees
    ? rawUserIds.filter(
        (id) =>
          id === ALL_USERS_OPTION_ID ||
          availableEmployees.some((e) => e.id === id)
      )
    : rawUserIds;

  const initialValues: Partial<IStatisticsFormModel> | undefined =
    hasUrlParams && parsedType != null
      ? {
          type: parsedType,
          dateFrom: parsedDateFrom,
          dateTo: parsedDateTo,
          userIds: rawUserIds,
          hallIds: rawHallIds,
        }
      : undefined;

  const form = useStatisticsForm(
    availableEmployees,
    initialValues,
    canSeeAllEmployees,
    currentUser?.id
  );

  // When employees load and we have valid URL params, auto-navigate to ViewStatistics
  useEffect(() => {
    if (!hasUrlParams || !availableEmployees || hasAutoNavigated.current) return;
    if (parsedType == null || !urlDateFrom || !urlDateTo) return;

    hasAutoNavigated.current = true;
    const href = constructViewStatisticsHref(
      parsedType,
      urlDateFrom,
      urlDateTo,
      validUserIds,
      rawHallIds
    );
    navigate(href, { replace: true });
  }, [availableEmployees]);

  // Sync form values to URL query params on every change
  const { type, dateFrom, dateTo, userIds, hallIds } = form.watch();
  const selectedType = type as StatisticsType;

  // Stable string deps to avoid re-running on reference changes
  const userIdsStr = (userIds as any[] ?? []).join(",");
  const hallIdsStr = ((hallIds as any[]) ?? []).join(",");
  const dateFromMs = dateFrom?.getTime() ?? "";
  const dateToMs = dateTo?.getTime() ?? "";

  useEffect(() => {
    if (!type || !dateFrom || !dateTo) return;

    const dateFromFormatted = dateUtility.formatDateForHref(dateFrom);
    const dateToFormatted = dateUtility.formatDateForHref(dateTo);
    if (!dateFromFormatted || !dateToFormatted) return;

    // Cross-reference with availableEmployees to ensure only numeric IDs go into URL
    // (guards against form storing names or unexpected values). The ALL_USERS_OPTION_ID
    // sentinel is handled separately via isAllUsersSelected below.
    const userIdsArr = (userIds as any[]) ?? [];
    const fallbackToken =
      canSeeAllEmployees || currentUser?.id == null
        ? ALL_USERS_URL_TOKEN
        : currentUser.id.toString();
    const usersForUrl =
      isAllUsersSelected(userIdsArr as number[]) && canSeeAllEmployees
        ? ALL_USERS_URL_TOKEN
        : (availableEmployees
            ? userIdsArr
                .map((v) => availableEmployees.find((e) => e.id === v || e.name === v)?.id)
                .filter((id): id is number => id != null && id > 0)
            : userIdsArr.filter(
                (v) => typeof v === "number" && !isNaN(v) && v > 0
              )
          ).join(",") || fallbackToken;

    const hallIdsArr = (hallIds as any[]) ?? [];
    const hallsForUrl = isAllHallsSelected(hallIdsArr as number[])
      ? ALL_HALLS_URL_TOKEN
      : hallIdsArr
          .filter((v) => typeof v === "number" && v > 0)
          .join(",") || ALL_HALLS_URL_TOKEN;

    setSearchParams(
      {
        type: type.toString(),
        dateFrom: dateFromFormatted,
        dateTo: dateToFormatted,
        userIds: usersForUrl,
        hallIds: hallsForUrl,
      },
      { replace: true }
    );
  }, [type, dateFromMs, dateToMs, userIdsStr, hallIdsStr, availableEmployees]);

  const onSave: SubmitHandler<IStatisticsFormModel> = async (data) => {
    const dateFromFormatted = dateUtility.formatDateForHref(data.dateFrom);
    const dateToFormatted = dateUtility.formatDateForHref(data.dateTo);

    const viewStatisticsHref = constructViewStatisticsHref(
      data.type,
      dateFromFormatted ?? "",
      dateToFormatted ?? "",
      (data.userIds as number[]) ?? [],
      (data.hallIds as number[]) ?? []
    );
    navigate(viewStatisticsHref);
  };

  return {
    statisticTypeOptions,
    form,
    availableEmployees,
    availableHalls,
    tenantUsesMoreThanOneHall,
    canSeeAllEmployees,
    selectedType,
    onSave,
  };
};
